"use client"
import { useState, useEffect, useRef } from "react"
import Hero from "@/components/home/hero"
import Features from "@/components/features"
import { TestimonialsSection } from "@/components/testimonials"
import { NewReleasePromo } from "@/components/new-release-promo"
import { FAQSection } from "@/components/faq-section"
import { PricingSection } from "@/components/pricing-section"
import { StickyFooter } from "@/components/sticky-footer"
import { Button } from "@/components/ui/button"
import { Menu } from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import { supabase } from "@/lib/supabase"
import { useNavigate } from "react-router-dom"
import { loadStripe } from '@stripe/stripe-js'
import { Music, TrendingUp, Heart, Mic2 } from "lucide-react"
import { motion, useInView } from "framer-motion"
import { FollowerPointerCard } from "@/components/ui/following-pointer"

const amplyLogo = new URL('../assets/amply.png', import.meta.url).href
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)

const About = () => {
    const ref = useRef(null)
  const isInView = useInView(ref, { once: true, amount: 0.3 })
  const [isTrackingHovering, setIsTrackingHovering] = useState(false)
  const [isConnectionsHovering, setIsConnectionsHovering] = useState(false)
  const [isImpactHovering, setIsImpactHovering] = useState(false)
  const [isProblemHovering, setIsProblemHovering] = useState(false)
  const { user } = useAuth()
  const navigate = useNavigate()
  const [plans, setPlans] = useState([])
  const FREE_PLAN_ID = '6d4ecf79-200c-4efc-85a9-e1205386d362'
  const [subscriptionPlanId, setSubscriptionPlanId] = useState(FREE_PLAN_ID)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const root = window.document.documentElement
    root.classList.remove("light", "system")
    root.classList.add("dark")
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Load subscription plans
  useEffect(() => {
    supabase
      .from('plans')
      .select('*')
      .then(({ data, error }) => {
        if (error) console.error('Error loading plans:', error)
        else setPlans(data)
      })
  }, [])
  
  // Load current user's subscription
  useEffect(() => {
    if (!user) return // no user: keep free plan as owned
    supabase
      .from('subscriptions')
      .select('plan_id')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) console.error('Error loading subscription:', error)
        setSubscriptionPlanId(data?.plan_id || FREE_PLAN_ID)
      })
  }, [user])

  // Subscribe to a plan: open Stripe Checkout Session
  const handleSubscribe = async (plan) => {
    if (!user) {
      navigate('/login')
      return
    }
    // Free plan: if not logged in, send user to register; if logged in, upsert free subscription
    if (plan.price_cents === 0) {
      if (!user) {
        navigate('/register')
        return
      }
      // Upsert free subscription
      try {
        const { error: freeErr } = await supabase
          .from('subscriptions')
          .upsert(
            { user_id: user.id, plan_id: plan.id, status: 'active' },
            { onConflict: 'user_id' }
          )
        if (freeErr) {
          console.error('Free subscription error:', freeErr)
        } else {
          alert('Subscribed to free plan!')
          navigate('/settings')
        }
      } catch (err) {
        console.error('Error upserting free subscription:', err)
      }
      return
    }
    // Paid plan: call backend to create session (and upsert subscription)
    try {
      console.log('Requesting checkout session for plan:', plan)
  const response = await fetch('https://backend.amply.buildfast.us/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId: plan.stripe_price_id, userId: user.id, ref: localStorage.getItem('referrerId') || '' }),
      })
      const { url, error } = await response.json()
      if (error) {
        console.error('Checkout session error:', error)
        alert(error)
        return
      }
      // Redirect to Stripe Checkout
      window.location.href = url
    } catch (err) {
      console.error('Network error creating checkout session:', err)
    }
  }

  return (
  <div className="w-full relative text-white">
      {/* Pearl Mist Background with Top Glow */}
      <div
        className="absolute inset-0 z-0 bg-[#000000]"
        style={{
          background: "radial-gradient(ellipse 50% 35% at 50% 0%, rgba(0,0,0,0.8), rgba(255,255,255,0.1) 60%), #000",
        }}
      />

      {/* Desktop Header */}
      <header
        className={`sticky top-4 z-[9999] mx-auto hidden w-full flex-row items-center justify-between self-start rounded-full bg-transparent md:flex backdrop-blur-sm border border-[#ebcebc] shadow-lg transition-all duration-300 ${
          isScrolled ? "max-w-3xl px-2" : "max-w-5xl px-4"
        } py-2`}
        style={{
          willChange: "transform",
          transform: "translateZ(0)",
          backfaceVisibility: "hidden",
          perspective: "1000px",
        }}
      >
        <a
          className={`z-50 flex items-center justify-center gap-2 transition-all duration-300 ${
            isScrolled ? "ml-4" : ""
          }`}
          href="/"
        >
                <img 
                src={amplyLogo}
                className="w-8 h-8"
                alt="Amply Logo"
                />
        </a>

  <div className="absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 text-sm font-medium text-white transition duration-200 hover:text-white md:flex md:space-x-2">
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="./#features"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Features</span>
          </a>
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/about"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">About</span>
          </a>
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/pricing"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Pricing</span>
          </a>
                  <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/contact"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Contact Us</span>
          </a>
          
        </div>

        <div className="flex items-center gap-4">
          {user ? (
            <a
              href="/dashboard"
              className="rounded-full font-bold relative cursor-pointer transition duration-300 inline-block text-center bg-[#e3c5b3] text-white shadow-lg hover:shadow-xl hover:scale-105 px-6 py-3 text-base"
            >
              Dashboard
            </a>
          ) : (
            <>
              <a
                href="/login"
                className="rounded-full border border-[#e3c5b3] font-bold relative cursor-pointer transition duration-200 inline-block text-center text-primary-foreground px-4 py-2 text-sm"
              >
                Log In
              </a>
              <a
                href="/register"
                className="rounded-full font-bold relative cursor-pointer transition duration-300 inline-block text-center bg-[#e3c5b3] text-white shadow-lg hover:shadow-xl hover:scale-105 px-6 py-3 text-base"
              >
                Sign Up
              </a>
            </>
          )}
        </div>
      </header>

      {/* Mobile Header */}
  <header className="sticky top-4 z-[9999] mx-4 flex w-auto flex-row items-center justify-between rounded-full bg-transparent backdrop-blur-sm border border-[#ebcebc] shadow-lg md:hidden px-4 py-3">
        <a
          className="flex items-center justify-center gap-2"
          href="./"
        >
                <img 
                src={amplyLogo}
                className="w-8 h-8"
                alt="Amply Logo"
                />
        </a>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-background/50 border border-border/50 transition-colors hover:bg-background/80"
          aria-label="Toggle menu"
        >
          <div className="flex flex-col items-center justify-center w-5 h-5 space-y-1">
            {isMobileMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
             <Menu className="w-5 h-5 text-foreground" />
            )}
          </div>
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
  <div className="fixed inset-0 z-[9998] bg-transparent backdrop-blur-sm md:hidden">
    <div className="absolute top-28 left-4 right-4 bg-transparent/80 backdrop-blur-md border border-border/50 rounded-2xl shadow-2xl p-6">
      <nav className="flex flex-col space-y-4">
        <a
            href="./#features"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          Features
        </a>
        <a
          href="/about"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          About
        </a>
        <a
          href="/pricing"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          Pricing
        </a>
               <a
          href="/contact"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          Contact Us
        </a>
        <div className="border-t border-border/50 pt-4 mt-4 flex flex-col space-y-3">
          {user ? (
            <a
              href="/dashboard"
              className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
            >
              Dashboard
            </a>
          ) : (
            <>
              <a
                href="/login"
                className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
              >
                Log In
              </a>
              <a
                href="/register"
                className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
              >
                Sign Up
              </a>
            </>
          )}
        </div>
      </nav>
    </div>
  </div>
)}
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('/abstract-music-waves-pattern.jpg')] opacity-5"></div>
        <div className="pointer-events-none absolute left-1/2 top-0 z-0 h-80 w-[60vw] -translate-x-1/2 bg-gradient-radial from-[#e3c5b3]/30 via-[#e3c5b3]/10 to-transparent opacity-60 blur-2xl"></div>
        <div className="absolute top-0 left-1/2 w-full max-w-4xl -translate-x-1/2 h-[2px]">
          <div
            className="w-full h-full bg-gradient-to-r from-transparent via-[#e3c5b3] to-transparent animate-pulse"
            style={{ opacity: 0.5 }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="flex justify-center mb-8"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-[#e3c5b3]/20 rounded-full blur-xl"></div>
                <div className="relative bg-[#e3c5b3]/10 p-6 rounded-full">
                  <Music className="h-16 w-16 text-[#e3c5b3]" />
                </div>
              </div>
            </motion.div>
            <motion.h1
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-balance mb-6 text-white"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={{
                textShadow: "0 0 16px #e3c5b3, 0 0 4px white",
              }}
            >
              About <span className="text-[#e3c5b3]">Amply</span>
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl text-white/70 text-balance max-w-3xl mx-auto mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Helping artists build lasting connections with their fans through intelligent CRM designed for the music
              industry
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Interactive Problem Statement */}
      <section className="text-white relative overflow-hidden py-12 sm:py-24 md:py-32">
        <div className="pointer-events-none absolute left-1/2 top-0 z-0 h-80 w-[60vw] -translate-x-1/2 bg-gradient-radial from-red-600/20 via-red-600/5 to-transparent opacity-40 blur-2xl"></div>
        <div className="absolute top-0 left-1/2 w-full max-w-4xl -translate-x-1/2 h-[2px]">
          <div
            className="w-full h-full bg-gradient-to-r from-transparent via-red-600 to-transparent animate-pulse"
            style={{ opacity: 0.3 }}
          />
        </div>

        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.5, delay: 0 }}
          className="container mx-auto flex flex-col items-center gap-6 sm:gap-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6"
          >
            <TrendingUp className="w-4 h-4 text-red-500" />
            <span className="text-sm font-medium text-white/70">The Problem</span>
          </motion.div>

          <h2 className="mb-5 text-center text-4xl font-semibold tracking-tighter md:text-[54px] md:leading-[60px] relative z-10 text-white">
            The Challenge Artists Face
          </h2>

          <FollowerPointerCard
            title={
              <div className="flex items-center gap-2">
                <span>⚠️</span>
                <span>Fan Retention Crisis</span>
              </div>
            }
          >
            <div className="cursor-none">
              <motion.div
                className="group border-red-600/40 text-white relative col-span-12 flex flex-col overflow-hidden rounded-xl border-2 p-6 shadow-xl transition-all ease-in-out max-w-4xl mx-auto bg-black/50"
                onMouseEnter={() => setIsProblemHovering(true)}
                onMouseLeave={() => setIsProblemHovering(false)}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                whileHover={{
                  scale: 1.02,
                  borderColor: "rgb(220 38 38)",
                  boxShadow: "0 0 30px rgba(220, 38, 38, 0.2)",
                }}
              >
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <h3 className="text-2xl leading-none font-semibold tracking-tight mb-4 text-white">
                      Fan Discovery vs. Retention
                    </h3>
                    <p className="text-white/70 mb-4">
                      While managing artists, I witnessed a frustrating pattern: fans would discover and enjoy new
                      music, but they often never stuck around to hear the next release.
                    </p>
                    <p className="text-white/70 mb-6">
                      Artists were losing out, not because their songs weren't good, but because they had{" "}
                      <strong className="text-white">no clear way to stay connected with listeners</strong>.
                    </p>

                    <div className="grid grid-cols-2 gap-4">
                      <motion.div
                        className="text-center p-4 bg-red-600/10 rounded-lg border border-red-600/20"
                        animate={isProblemHovering ? { scale: [1, 1.05, 1] } : { scale: 1 }}
                        transition={{ duration: 1, repeat: isProblemHovering ? Number.POSITIVE_INFINITY : 0 }}
                      >
                        <div className="text-2xl font-bold text-red-500 mb-1">73%</div>
                        <div className="text-sm text-white/70">Fans lost after first listen</div>
                      </motion.div>
                      <motion.div
                        className="text-center p-4 bg-red-600/10 rounded-lg border border-red-600/20"
                        animate={isProblemHovering ? { scale: [1, 1.05, 1] } : { scale: 1 }}
                        transition={{
                          duration: 1,
                          delay: 0.2,
                          repeat: isProblemHovering ? Number.POSITIVE_INFINITY : 0,
                        }}
                      >
                        <div className="text-2xl font-bold text-red-500 mb-1">85%</div>
                        <div className="text-sm text-white/70">Artists lack fan data</div>
                      </motion.div>
                    </div>
                  </div>

                  <div className="flex grow items-center justify-center select-none relative min-h-[300px]">
                    <div className="relative w-full h-[300px] rounded-xl overflow-hidden bg-gradient-to-br from-red-600/10 to-red-800/10">
                      {/* Fan Loss Visualization */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative">
                          {/* Central Artist */}
                          <motion.div
                            className="w-16 h-16 bg-[#e3c5b3] rounded-full flex items-center justify-center text-black font-bold shadow-lg relative z-10"
                            animate={isProblemHovering ? { scale: [1, 1.1, 1] } : { scale: 1 }}
                            transition={{ duration: 1, repeat: isProblemHovering ? Number.POSITIVE_INFINITY : 0 }}
                          >
                            🎵
                          </motion.div>

                          {/* Disappearing Fans */}
                          {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => {
                            const angle = index * 45 * (Math.PI / 180)
                            const radius = 80
                            const x = Math.cos(angle) * radius
                            const y = Math.sin(angle) * radius

                            return (
                              <motion.div
                                key={index}
                                className="absolute w-8 h-8 bg-white/20 rounded-full flex items-center justify-center shadow-md border-2 border-red-600/30"
                                style={{
                                  transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                                }}
                                initial={{ opacity: 1, scale: 1 }}
                                animate={
                                  isProblemHovering
                                    ? {
                                        opacity: [1, 0.3, 0],
                                        scale: [1, 0.8, 0.5],
                                        x: [0, x * 1.5],
                                        y: [0, y * 1.5],
                                      }
                                    : { opacity: 0.7, scale: 0.9 }
                                }
                                transition={{
                                  duration: 2,
                                  delay: index * 0.2,
                                  repeat: isProblemHovering ? Number.POSITIVE_INFINITY : 0,
                                  repeatDelay: 1,
                                }}
                              >
                                <span className="text-xs">👤</span>
                              </motion.div>
                            )
                          })}

                          {/* Connection Lines that Break */}
                          <motion.div
                            className="absolute inset-0"
                            initial={{ opacity: 0 }}
                            animate={isProblemHovering ? { opacity: 1 } : { opacity: 0 }}
                            transition={{ duration: 0.5 }}
                          >
                            <svg width="200" height="200" className="absolute -translate-x-1/2 -translate-y-1/2">
                              {[0, 1, 2, 3, 4, 5, 6, 7].map((index) => {
                                const angle = index * 45 * (Math.PI / 180)
                                const radius = 80
                                const x = Math.cos(angle) * radius + 100
                                const y = Math.sin(angle) * radius + 100

                                return (
                                  <motion.line
                                    key={index}
                                    x1="100"
                                    y1="100"
                                    x2={x}
                                    y2={y}
                                    stroke="rgb(220 38 38)"
                                    strokeWidth="2"
                                    strokeDasharray="3 3"
                                    initial={{ pathLength: 1, opacity: 1 }}
                                    animate={
                                      isProblemHovering
                                        ? { pathLength: [1, 0.5, 0], opacity: [1, 0.5, 0] }
                                        : { pathLength: 0.3, opacity: 0.3 }
                                    }
                                    transition={{
                                      duration: 2,
                                      delay: index * 0.2,
                                      repeat: isProblemHovering ? Number.POSITIVE_INFINITY : 0,
                                      repeatDelay: 1,
                                    }}
                                  />
                                )
                              })}
                            </svg>
                          </motion.div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </FollowerPointerCard>
        </motion.div>
      </section>

      {/* Solution Section */}
      <section className="text-white relative overflow-hidden py-12 sm:py-24 md:py-32">
        <div className="pointer-events-none absolute left-1/2 top-0 z-0 h-80 w-[60vw] -translate-x-1/2 bg-gradient-radial from-[#e3c5b3]/30 via-[#e3c5b3]/10 to-transparent opacity-60 blur-2xl"></div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="container mx-auto flex flex-col items-center gap-6 sm:gap-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#e3c5b3]/30 backdrop-blur-sm mb-6"
          >
            <Heart className="w-4 h-4 text-[#e3c5b3]" />
            <span className="text-sm font-medium text-white">Our Solution</span>
          </motion.div>

          <h2 className="mb-5 text-center text-4xl font-semibold tracking-tighter md:text-[54px] md:leading-[60px] relative z-10 text-white">
            That's Why We Built Amply
          </h2>

          <FollowerPointerCard
            title={
              <div className="flex items-center gap-2">
                <span>🚀</span>
                <span>Amply Features</span>
              </div>
            }
          >
            <div className="cursor-none">
              <div className="grid grid-cols-12 gap-4 justify-center max-w-6xl mx-auto">
                {/* Track Listeners Feature */}
                <motion.div
                  className="group border-[#e3c5b3]/40 text-white relative col-span-12 flex flex-col overflow-hidden rounded-xl border-2 p-6 shadow-xl transition-all ease-in-out md:col-span-6 xl:col-span-4 bg-black/50"
                  onMouseEnter={() => setIsTrackingHovering(true)}
                  onMouseLeave={() => setIsTrackingHovering(false)}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  whileHover={{
                    scale: 1.02,
                    borderColor: "#e3c5b3",
                    boxShadow: "0 0 30px rgba(227, 197, 179, 0.2)",
                  }}
                >
                  <div className="flex flex-col gap-4">
                    <h3 className="text-xl leading-none font-semibold tracking-tight text-white">
                      Boost Your Listeners
                    </h3>
                    <p className="text-sm text-white/70">
                      Through smart links or campaigns, track who listens to your music and how they engage with your releases.
                    </p>
                    <br/>
                  </div>
                  <div className="flex grow items-center justify-center select-none relative min-h-[200px]">
                    <div className="relative w-full h-[200px] rounded-xl overflow-hidden bg-gradient-to-br from-[#e3c5b3]/10 to-[#e3c5b3]/5">
                      {/* Listener Tracking Visualization */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white/90 rounded-lg p-4 backdrop-blur-sm w-full max-w-[200px]">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-black">Listeners</span>
                            <span className="text-xs text-black font-medium bg-[#e3c5b3]/20 px-2 py-1 rounded">
                              Live
                            </span>
                          </div>

                          <div className="space-y-2">
                            {["New", "Returning", "Engaged"].map((type, index) => (
                              <motion.div
                                key={type}
                                className="flex items-center justify-between"
                                initial={{ opacity: 0, x: -20 }}
                                animate={isTrackingHovering ? { opacity: 1, x: 0 } : { opacity: 0.7, x: -10 }}
                                transition={{ duration: 0.4, delay: index * 0.1 }}
                              >
                                <div className="flex items-center gap-2">
                                  <div
                                    className={`w-2 h-2 rounded-full ${index === 0 ? "bg-green-500" : index === 1 ? "bg-blue-500" : "bg-purple-500"}`}
                                  ></div>
                                  <span className="text-xs text-black">{type}</span>
                                </div>
                                <motion.span
                                  className="text-xs font-bold text-black"
                                  animate={isTrackingHovering ? { scale: [1, 1.1, 1] } : { scale: 1 }}
                                  transition={{
                                    duration: 1,
                                    delay: index * 0.2,
                                    repeat: isTrackingHovering ? Number.POSITIVE_INFINITY : 0,
                                  }}
                                >
                                  {index === 0 ? "1.2K" : index === 1 ? "847" : "623"}
                                </motion.span>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Build Connections Feature */}
                <motion.div
                  className="group border-[#e3c5b3]/40 text-white relative col-span-12 flex flex-col overflow-hidden rounded-xl border-2 p-6 shadow-xl transition-all ease-in-out md:col-span-6 xl:col-span-4 bg-black/50"
                  onMouseEnter={() => setIsConnectionsHovering(true)}
                  onMouseLeave={() => setIsConnectionsHovering(false)}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  whileHover={{
                    scale: 1.02,
                    borderColor: "#e3c5b3",
                    boxShadow: "0 0 30px rgba(227, 197, 179, 0.2)",
                  }}
                >
                  <div className="flex flex-col gap-4">
                    <h3 className="text-xl leading-none font-semibold tracking-tight text-white">Build Connections</h3>
                    <p className="text-sm text-white/70">
                      Create meaningful relationships with your audience through personalized outreach and targeted
                      communication.
                    </p>
                  </div>
                  <div className="flex grow items-center justify-center select-none relative min-h-[200px]">
                    <div className="relative w-full h-[200px] rounded-xl overflow-hidden bg-gradient-to-br from-[#e3c5b3]/10 to-[#e3c5b3]/5">
                      {/* Connection Building Visualization */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="relative">
                          {/* Central Artist */}
                          <motion.div
                            className="w-12 h-12 bg-[#e3c5b3] rounded-full flex items-center justify-center text-black font-bold shadow-lg relative z-10"
                            animate={isConnectionsHovering ? { scale: [1, 1.1, 1] } : { scale: 1 }}
                            transition={{ duration: 1, repeat: isConnectionsHovering ? Number.POSITIVE_INFINITY : 0 }}
                          >
                            🎤
                          </motion.div>

                          {/* Connected Fans */}
                          {[0, 1, 2, 3, 4, 5].map((index) => {
                            const angle = index * 60 * (Math.PI / 180)
                            const radius = 50
                            const x = Math.cos(angle) * radius
                            const y = Math.sin(angle) * radius

                            return (
                              <motion.div
                                key={index}
                                className="absolute w-8 h-8 bg-black/80 rounded-full flex items-center justify-center shadow-md border-2 border-[#e3c5b3]/50"
                                style={{
                                  transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                                }}
                                initial={{ opacity: 0, scale: 0 }}
                                animate={
                                  isConnectionsHovering
                                    ? {
                                        opacity: 1,
                                        scale: 1,
                                        rotate: [0, 360],
                                      }
                                    : { opacity: 0.7, scale: 0.8, rotate: 0 }
                                }
                                transition={{
                                  duration: 0.6,
                                  delay: index * 0.1,
                                  rotate: {
                                    duration: 3,
                                    repeat: isConnectionsHovering ? Number.POSITIVE_INFINITY : 0,
                                    ease: "linear",
                                  },
                                }}
                              >
                                <span className="text-xs text-white">💝</span>
                              </motion.div>
                            )
                          })}

                          {/* Connection Lines */}
                          <motion.div
                            className="absolute inset-0"
                            initial={{ opacity: 0 }}
                            animate={isConnectionsHovering ? { opacity: 1 } : { opacity: 0 }}
                            transition={{ duration: 0.5 }}
                          >
                            <svg width="120" height="120" className="absolute -translate-x-1/2 -translate-y-1/2">
                              {[0, 1, 2, 3, 4, 5].map((index) => {
                                const angle = index * 60 * (Math.PI / 180)
                                const radius = 50
                                const x = Math.cos(angle) * radius + 60
                                const y = Math.sin(angle) * radius + 60

                                return (
                                  <motion.line
                                    key={index}
                                    x1="60"
                                    y1="60"
                                    x2={x}
                                    y2={y}
                                    stroke="#e3c5b3"
                                    strokeWidth="2"
                                    strokeDasharray="3 3"
                                    initial={{ pathLength: 0 }}
                                    animate={isConnectionsHovering ? { pathLength: 1 } : { pathLength: 0 }}
                                    transition={{ duration: 1, delay: index * 0.1 }}
                                  />
                                )
                              })}
                            </svg>
                          </motion.div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Maximize Impact Feature */}
                <motion.div
                  className="group border-[#e3c5b3]/40 text-white relative col-span-12 flex flex-col overflow-hidden rounded-xl border-2 p-6 shadow-xl transition-all ease-in-out md:col-span-6 xl:col-span-4 bg-black/50"
                  onMouseEnter={() => setIsImpactHovering(true)}
                  onMouseLeave={() => setIsImpactHovering(false)}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  whileHover={{
                    scale: 1.02,
                    borderColor: "#e3c5b3",
                    boxShadow: "0 0 30px rgba(227, 197, 179, 0.2)",
                  }}
                >
                  <div className="flex flex-col gap-4">
                    <h3 className="text-xl leading-none font-semibold tracking-tight text-white">Maximize Impact</h3>
                    <p className="text-sm text-white/70">
                      Turn every release into an opportunity for growth. Keep fans engaged and excited for what's coming
                      next.
                    </p>
                  </div>
                  <div className="flex grow items-center justify-center select-none relative min-h-[200px]">
                    <div className="relative w-full h-[200px] rounded-xl overflow-hidden bg-gradient-to-br from-[#e3c5b3]/10 to-[#e3c5b3]/5">
                      {/* Impact Visualization */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white/90 rounded-lg p-4 backdrop-blur-sm w-full max-w-[180px]">
                          <div className="text-center mb-3">
                            <span className="text-sm font-medium text-black">Release Impact</span>
                          </div>

                          <div className="flex items-end gap-1 h-16 mb-3">
                            {[30, 45, 60, 80, 95, 75, 90].map((height, index) => (
                              <motion.div
                                key={index}
                                className="bg-[#e3c5b3] rounded-sm flex-1"
                                initial={{ height: 0 }}
                                animate={isImpactHovering ? { height: `${height}%` } : { height: "20%" }}
                                transition={{ duration: 0.8, delay: index * 0.1 }}
                              />
                            ))}
                          </div>

                          <motion.div
                            className="text-center p-2 bg-[#e3c5b3]/10 rounded-lg"
                            animate={isImpactHovering ? { scale: [1, 1.05, 1] } : { scale: 1 }}
                            transition={{ duration: 1, repeat: isImpactHovering ? Number.POSITIVE_INFINITY : 0 }}
                          >
                            <div className="text-xs font-medium text-[#e3c5b3]">Growth Rate</div>
                            <div className="text-lg font-bold text-[#e3c5b3]">+247%</div>
                          </motion.div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </FollowerPointerCard>
        </motion.div>
      </section>
      <NewReleasePromo/>     
      <br/>
      <br/>
      <br/>
      <br/>
      <br/>
      
    </div>
  )
}

export default About;
